import type { ReactNode } from "react";

import { chartTheme } from "./chartTheme";

// The ruling has FOUR statuses. `platform` was missing here, so every row the
// backend marks `platform` — which is every MEASURED row — fell through the
// screen's mapping to NOT_MEASURED and printed "not measured" beside a real figure.
export type CascadeRowStatus =
  | "engine" | "platform" | "platform_estimate" | "NOT_MEASURED";

export interface CascadeStep {
  label: string;
  value: number | null;
  status: CascadeRowStatus;
}

export interface LossCascadeProps {
  unit: string;
  steps: CascadeStep[];
  renderValue?: (step: CascadeStep) => ReactNode;
}

const STATUS_LABEL: Record<CascadeRowStatus, string> = {
  engine: "engine",
  platform: "platform",
  platform_estimate: "platform estimate",
  NOT_MEASURED: "not measured",
};

export function LossCascade({ unit, steps, renderValue }: LossCascadeProps) {
  const theme = chartTheme();
  const maxAbs = Math.max(1, ...steps.map((s) => Math.abs(s.value ?? 0)));

  return (
    <table className="table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.82rem" }}>
      <caption className="mb-2 text-left font-medium">Loss cascade ({unit})</caption>
      <thead className="sr-only">
        <tr>
          <th scope="col">Step</th>
          <th scope="col">Loss</th>
          <th scope="col">Status</th>
        </tr>
      </thead>
      <tbody>
        {steps.map((step, i) => (
          <tr key={`${i}-${step.label}`} style={{ borderBottom: `1px solid ${theme.grid}` }}>
            <th scope="row" style={{ padding: "4px 8px 4px 0", textAlign: "left", fontWeight: 400 }}>
              {step.label}
            </th>
            <td style={{ width: "50%", padding: "4px 0" }}>
              {step.value === null ? (
                <span style={{ color: theme.textMuted }}>not measured</span>
              ) : (
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    aria-hidden
                    style={{
                      display: "inline-block",
                      height: 8,
                      borderRadius: 2,
                      backgroundColor: theme.bar,
                      width: `${Math.round((Math.abs(step.value) / maxAbs) * 100)}%`,
                    }}
                  />
                  {renderValue ? (
                    renderValue(step)
                  ) : (
                    <span>
                      {step.value.toLocaleString()} {unit}
                    </span>
                  )}
                </span>
              )}
            </td>
            <td style={{ padding: "4px 0 4px 8px", textAlign: "right", color: theme.textMuted }}>
              {STATUS_LABEL[step.status]}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
